/**
 * UpgradeLeadDialog — modal that captures interest from trial admins.
 * Replaces the previous Trial Banner "Upgrade plan" placeholder which
 * only navigated to /settings. On submit we POST to /api/trial/leads
 * (tenant-scoped) and the backend fires a Resend notification to every
 * platform-admin inbox so sales can follow up.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle2, Send } from 'lucide-react';
import api from '../lib/api';

const TEAM_SIZES = ['1-5', '6-20', '21-50', '51-200', '200+'];

export default function UpgradeLeadDialog({ open, onOpenChange, defaultOrgName = '', defaultName = '', defaultEmail = '' }) {
  const [form, setForm] = useState({
    organisation_name: defaultOrgName,
    contact_name: defaultName,
    email: defaultEmail,
    team_size: '',
    requirements: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Re-sync defaults when the modal is reopened (user info may have
  // loaded after first mount).
  React.useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        organisation_name: f.organisation_name || defaultOrgName,
        contact_name: f.contact_name || defaultName,
        email: f.email || defaultEmail,
      }));
      setSubmitted(false);
    }
  }, [open, defaultOrgName, defaultName, defaultEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.team_size) { toast.error('Pick your approximate team size'); return; }
    setSubmitting(true);
    try {
      await api.post('/trial/leads', form);
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => { onOpenChange?.(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]" aria-describedby="upgrade-lead-desc">
        {submitted ? (
          <div className="py-6 flex flex-col items-center text-center" data-testid="upgrade-lead-thanks">
            <CheckCircle2 className="w-10 h-10 text-[#1ABC9C] mb-3" />
            <h3 className="text-lg font-semibold">We've got your details</h3>
            <p className="text-sm text-[#C7C7C7] mt-1 max-w-sm">
              Someone from our team will reach out within one working day to walk you through
              the next steps.
            </p>
            <Button
              className="mt-5 bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
              onClick={close}
              data-testid="upgrade-lead-close-btn"
            >
              Back to Elara
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Tell us about your team</DialogTitle>
              <DialogDescription id="upgrade-lead-desc" className="text-[#C7C7C7]">
                We'll follow up to confirm pricing and set up your paid workspace. No credit card
                required — this just lets us tailor the setup to your team.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="upgrade-lead-form">
              <div className="space-y-2">
                <Label className="text-[#F2F2F2]">Organisation name</Label>
                <Input
                  value={form.organisation_name}
                  onChange={(e) => setForm((f) => ({ ...f, organisation_name: e.target.value }))}
                  required
                  className="bg-[#1A1A1A] border-[#3D3D3D]"
                  data-testid="upgrade-lead-org"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-[#F2F2F2]">Your name</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                    required
                    className="bg-[#1A1A1A] border-[#3D3D3D]"
                    data-testid="upgrade-lead-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#F2F2F2]">Work email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    required
                    className="bg-[#1A1A1A] border-[#3D3D3D]"
                    data-testid="upgrade-lead-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#F2F2F2]">Approximate team size</Label>
                <Select value={form.team_size} onValueChange={(v) => setForm((f) => ({ ...f, team_size: v }))}>
                  <SelectTrigger className="bg-[#1A1A1A] border-[#3D3D3D]" data-testid="upgrade-lead-size">
                    <SelectValue placeholder="Pick a range" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2B2B2B] border-white/10">
                    {TEAM_SIZES.map((s) => (
                      <SelectItem key={s} value={s} className="text-[#F2F2F2]">
                        {s} people
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#F2F2F2]">Anything specific you need? (optional)</Label>
                <Textarea
                  value={form.requirements}
                  onChange={(e) => setForm((f) => ({ ...f, requirements: e.target.value }))}
                  className="bg-[#1A1A1A] border-[#3D3D3D] min-h-24"
                  placeholder="e.g. SSO, specific compliance, bulk shift imports, Power BI integration…"
                  data-testid="upgrade-lead-requirements"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close} className="text-[#C7C7C7]">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                  disabled={submitting}
                  data-testid="upgrade-lead-submit"
                >
                  {submitting ? 'Sending…' : (<><Send className="w-4 h-4 mr-2" />Send details</>)}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
